import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import UAEFlag from "./UAEFlag";

// Grouped menu (31 Oct 2026, Yitayal: "organize the burger — too many things, gather them").
// `href` = flat/static page served outside the SPA router (full page load); `to` = React route.
const NAV_GROUPS = [
  { title: "Buy", items: [
    { to: "/search", label: "Search properties" },
    { to: "/search?type=ready", label: "Ready to move in" },
    { to: "/offplan", label: "Off-plan launches" },
    { to: "/listings", label: "All listings" },
  ] },
  { title: "Our residences", items: [
    { to: "/bay-views", label: "Bay Views", featured: true },
    { to: "/bay-residences", label: "Bay Residences", featured: true },
    { to: "/marina-shores", label: "Marina Shores", featured: true },
  ] },
  { title: "Where", items: [
    { href: "/for-sale", label: "Dubai" },
    { href: "/why-invest-in-ras-al-khaimah", label: "Ras Al Khaimah" },
    { to: "/areas", label: "Area guides" },
    { to: "/developers", label: "Developers" },
  ] },
  { title: "Research", items: [
    { to: "/market-intelligence", label: "Market intel" },
    { to: "/insights", label: "Insights" },
    { href: "/dubai-real-estate/rak-vs-dubai/wynn-al-marjan-island-tracker", label: "Wynn tracker" },
    { href: "/bayn-ora-vs-palm-jebel-ali", label: "Bayn vs Palm Jebel Ali" },
    { href: "/research", label: "All research" },
  ] },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pathname.startsWith("/crm")) return null;

  // Over the hero on /: transparent + bone text. Elsewhere (or after scroll): header-bar bg + ink text.
  const transparent = isHome && !scrolled;
  const textCls = transparent ? "text-bone" : "text-ink";
  const mutedCls = transparent ? "text-bone/75 hover:text-bone" : "text-ink/65 hover:text-ink";
  const shellCls = transparent
    ? "bg-transparent border-transparent"
    : "bg-header-bar/95 backdrop-blur-xl border-sand-dark shadow-subtle";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 border-b transition-all duration-500 ease-out ${shellCls} ${textCls}`}
      data-testid="site-header"
    >
      <div className={`px-6 md:px-12 lg:px-20 flex items-center justify-between transition-all ${transparent ? "h-24" : "h-20"}`}>
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img
            src="/sofia-logo.webp"
            width={480}
            height={320}
            alt="Sofia Sands Realty — RERA 41793"
            className={`w-16 h-16 md:w-20 md:h-20 object-contain transition-opacity ${transparent ? "" : ""}`}
            data-testid="brand-logo"
          />
          <span className="flex flex-col leading-tight">
            <span className="font-display text-[1.4rem] md:text-[1.55rem] leading-none tracking-tighter">Sofia Sands</span>
            <span className={`text-[0.58rem] tracking-[0.22em] uppercase font-medium mt-1 ${transparent ? "text-bone/65" : "text-ink/55"}`} data-testid="brand-rera-stamp">
              Realty &middot; RERA 41793
            </span>
          </span>
        </Link>

        {/* UAE Flag - waving animation */}
        {/* Minimal bar: logo (left) + valuable CTA + Menu burger (right).
            Full navigation lives in the overlay so the bar never congests. */}
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center mr-1">
            <UAEFlag size={26} />
          </div>
          <Link
            to="/find"
            className={`hidden md:inline-flex items-center h-11 px-6 text-[0.72rem] tracking-caps uppercase font-medium transition-colors border ${
              transparent
                ? "border-bone text-bone hover:bg-bone hover:text-ink"
                : "border-ink bg-ink text-bone hover:bg-ink-light hover:text-gold"
            }`}
            data-testid="header-cta-find"
          >
            Find Your Residence
          </Link>
          <button
            className={`p-2 inline-flex items-center gap-2 ${mutedCls}`}
            onClick={() => setOpen(!open)}
            data-testid="menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <span className="hidden sm:inline text-[0.72rem] tracking-caps uppercase font-medium">
              {open ? "Close" : "Menu"}
            </span>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="bg-header-bar border-t border-sand-dark text-ink" data-testid="site-menu">
          <div className="px-6 md:px-12 lg:px-20 py-10 max-w-6xl mx-auto">
            <nav className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-10" aria-label="Primary">
              {NAV_GROUPS.map((g) => (
                <div key={g.title}>
                  <div className="text-[0.62rem] tracking-[0.16em] uppercase text-ink/45 mb-4">{g.title}</div>
                  <ul className="space-y-3">
                    {g.items.map((n) => {
                      const cls = `font-display text-[1.2rem] md:text-[1.35rem] leading-tight hover:text-gold transition-colors ${n.featured ? "text-gold" : "text-ink"}`;
                      const tid = `nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                      return (
                        <li key={n.label}>
                          {n.href ? (
                            <a href={n.href} onClick={() => setOpen(false)} className={cls} data-testid={tid}>{n.label}</a>
                          ) : (
                            <Link to={n.to} onClick={() => setOpen(false)} className={cls} data-testid={tid}>{n.label}</Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
            <div className="mt-10 pt-8 border-t border-sand-dark flex flex-wrap items-center justify-between gap-6">
              <Link
                to="/find"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center h-12 px-8 bg-ink text-bone text-[0.72rem] tracking-caps uppercase font-medium hover:bg-ink-light hover:text-gold transition-colors"
                data-testid="menu-cta-find"
              >
                Find Your Residence
              </Link>
              <div className="flex items-center gap-6">
                <Link to="/about" onClick={() => setOpen(false)} className="text-[0.72rem] tracking-caps uppercase text-ink/70 hover:text-gold" data-testid="nav-about">About</Link>
                <div className="stamp stamp-rera">RERA 41793 · Dubai &amp; RAK</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
